import { FileFragment, MessageFragment, ReactionType } from "gql/graphql";
import { useMixpanel } from "modules/providers/mixpanel-provider";
import {
  MIXPANEL_EVENT_TYPE,
  PERSON_COUNT_TYPE,
} from "modules/providers/mixpanel-provider/mixpanel-provider";
import CircularIconButton from "modules/_shared/buttons/circular-icon-button";
import React, { memo, useCallback, useMemo } from "react";
import {
  ReactionHeartColorIcon,
  ReactionHeartIcon,
  ReactionLaughingColorIcon,
  ReactionLaughingIcon,
  ReactionSurpriseColorIcon,
  ReactionSurpriseIcon,
  ReactionThumbsDownColorIcon,
  ReactionThumbsDownIcon,
  ReactionThumbsUpColorIcon,
  ReactionThumbsUpIcon,
} from "modules/icons";
import useEditMessageReaction from "modules/hooks/use-edit-message-reaction";
import { useAuth } from "modules/providers/auth-provider";

interface IProps {
  message: MessageFragment;
  file?: FileFragment;
  type: ReactionType;
}

const MessagePopoverReactionButton: React.FC<IProps> = ({
  message,
  file,
  type,
}) => {
  const { track, incrementPersonProperty } = useMixpanel();
  const { me } = useAuth();
  const reactionList = message.reactionList;

  const hasMe = useMemo(() => {
    if (!me) return false;
    if (file) {
      return reactionList.some(
        (obj) =>
          obj.type === type && obj.userId === me.id && obj.fileId === file.id,
      );
    }
    return reactionList.some(
      (obj) => obj.type === type && obj.userId === me.id && !obj.fileId,
    );
  }, [file, me, reactionList, type]);

  const [editMessageReactionAPI] = useEditMessageReaction(
    message,
    type,
    file?.id,
  );

  const onReactionClicked = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      editMessageReactionAPI();
      track(MIXPANEL_EVENT_TYPE.MESSAGE_REACTION, { "Emoji Type": type });
      incrementPersonProperty(PERSON_COUNT_TYPE.MESSAGE_REACTIONS);
    },
    [editMessageReactionAPI, incrementPersonProperty, track, type],
  );

  const icon = useMemo(() => {
    switch (type) {
      case ReactionType.Like:
        return hasMe ? (
          <ReactionThumbsUpColorIcon boxSize={6} />
        ) : (
          <ReactionThumbsUpIcon boxSize={6} />
        );
      case ReactionType.Dislike:
        return hasMe ? (
          <ReactionThumbsDownColorIcon boxSize={6} />
        ) : (
          <ReactionThumbsDownIcon boxSize={6} />
        );
      case ReactionType.Heart:
        return hasMe ? (
          <ReactionHeartColorIcon boxSize={6} />
        ) : (
          <ReactionHeartIcon boxSize={6} />
        );
      case ReactionType.Laughing:
        return hasMe ? (
          <ReactionLaughingColorIcon boxSize={6} />
        ) : (
          <ReactionLaughingIcon boxSize={6} />
        );
      case ReactionType.Surprise:
        return hasMe ? (
          <ReactionSurpriseColorIcon boxSize={6} />
        ) : (
          <ReactionSurpriseIcon boxSize={6} />
        );
      default:
        return hasMe ? (
          <ReactionThumbsUpColorIcon boxSize={6} />
        ) : (
          <ReactionThumbsUpIcon boxSize={6} />
        );
    }
  }, [hasMe, type]);

  return (
    <CircularIconButton
      size="xs"
      _hover={{ transform: "scale(1.2)" }}
      _active={{ transform: "unset" }}
      onClick={onReactionClicked}
      icon={icon}
      aria-label={type}
    />
  );
};

export default memo(MessagePopoverReactionButton);
